import { Component, Injector, OnInit } from '@angular/core';
import { BaseComponent } from '../../base/base.component';
import { AthleteService } from '../../../services/athlete/athlete.service';
import { CategoryService } from '../../../services/category/category.service';
import {
    ConvocationService,
} from '../../../services/convocation/convocation.service';
import { SeasonService } from '../../../services/season/season.service';
import {
    type ConvocatedUserPreview,
    type ConvocationRowItem,
} from './mister-convocation-shared';

@Component({
    selector: 'app-dashboard-mister',
    templateUrl: './dashboard-mister.component.html',
    styleUrls: ['./dashboard-mister.component.scss'],
})
export class DashboardMisterComponent extends BaseComponent implements OnInit {
    formOpen = false;

    convocations: ConvocationRowItem[] = [];
    /** Starts true so the first paint shows skeletons while categories + convocations load. */
    convocationsLoading = true;

    latestConvocation: ConvocationRowItem | null = null;
    latestInvitedUsers: ConvocatedUserPreview[] = [];
    latestInvitedLoading = false;
    statusUpdatingId: string | null = null;

    private categoryLabelById = new Map<string, string>();

    readonly companyId = localStorage.getItem('wisper_companyId') ?? '';

    constructor(
        public serviceInjector: Injector,
        private convocationService: ConvocationService,
        private categoryService: CategoryService,
        private seasonService: SeasonService,
        private athleteService: AthleteService,
    ) {
        super(serviceInjector);
    }

    async ngOnInit(): Promise<void> {
        await this.loadCategoryLabels();
        await this.loadConvocations();
    }

    private async loadCategoryLabels(): Promise<void> {
        this.categoryLabelById.clear();
        if (!this.companyId) {
            return;
        }
        try {
            const seasonsRes = await this.seasonService.getSeasons(
                this.companyId,
                { isCurrent: true },
            );
            const seasonId = seasonsRes?.data?.[0]?.id as string | undefined;
            const all = await this.categoryService.getCategories(
                this.companyId,
                false,
                seasonId ? { seasonId } : undefined,
            );
            for (const c of all) {
                if (!c.id) {
                    continue;
                }
                const label = c.parent
                    ? `${c.parent.name} - ${c.name}`
                    : c.name;
                this.categoryLabelById.set(c.id, label);
            }
        } catch {
            /* ignore */
        }
    }

    async loadConvocations(): Promise<void> {
        this.convocationsLoading = true;
        try {
            const res = await this.convocationService.listConvocations({
                $top: 100,
                $skip: 0,
            } as any);
            const rawRows = this.convocationService.extractRows(res);
            const mapped = rawRows
                .filter((r: any) => (r?.status ?? '').toLowerCase() !== 'deleted')
                .map((r: any) =>
                    this.convocationService.mapApiRecordToRow(
                        r,
                        r?.categoryId
                            ? this.categoryLabelById.get(r.categoryId)
                            : undefined,
                    ),
                )
                .sort(
                    (a, b) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime(),
                );
            this.convocations = mapped;
            this.latestConvocation = mapped.length > 0 ? mapped[0] : null;
            void this.loadLatestInvitedPreviews();
        } finally {
            this.convocationsLoading = false;
        }
    }

    private async loadLatestInvitedPreviews(): Promise<void> {
        this.latestInvitedUsers = [];
        const lc = this.latestConvocation;
        const ids = lc?.users?.length ? lc.users : [];
        if (!ids.length) {
            return;
        }
        this.latestInvitedLoading = true;
        try {
            this.latestInvitedUsers = await this.resolveAthleteIdsToPreviews(
                ids,
            );
        } finally {
            this.latestInvitedLoading = false;
        }
    }

    private async resolveAthleteIdsToPreviews(
        ids: string[],
    ): Promise<ConvocatedUserPreview[]> {
        const out: ConvocatedUserPreview[] = [];
        for (const id of ids) {
            if (!id) {
                continue;
            }
            try {
                const a = await this.athleteService.getAthleteById(id);
                if (a?.id) {
                    const fullName =
                        `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() ||
                        '—';
                    out.push({
                        id: a.id,
                        fullName,
                        roleLabel: a.note ?? a.status,
                    });
                }
            } catch {
                /* skip */
            }
        }
        return out.sort((x, y) =>
            x.fullName.localeCompare(y.fullName, 'it'),
        );
    }

    async onNewConvocationSaved(_row: ConvocationRowItem): Promise<void> {
        await this.loadConvocations();
        this.formOpen = false;
    }

    async onApproveDraft(convocation: ConvocationRowItem): Promise<void> {
        await this.updateConvocationStatus(convocation, 'Pubblicata');
    }

    async onCancelDraft(convocation: ConvocationRowItem): Promise<void> {
        const confirmed = await this.showQuestionMessage();
        if (!confirmed) {
            return;
        }
        await this.updateConvocationStatus(convocation, 'Annullata');
    }

    private async updateConvocationStatus(
        convocation: ConvocationRowItem,
        status: string,
    ): Promise<void> {
        const userId = localStorage.getItem('wisper_userId') ?? '';
        if (!userId) {
            this.showErrorMessage('Sessione non valida. Effettua di nuovo l’accesso.');
            return;
        }
        if (!convocation.categoryId) {
            this.showErrorMessage(
                'Impossibile aggiornare la convocazione: categoria mancante.',
            );
            return;
        }

        this.statusUpdatingId = convocation.id;
        try {
            const payload = this.convocationService.buildApiPayloadFromRow(
                convocation,
                userId,
                status,
            );
            const res = await this.convocationService.updateConvocation(
                convocation.id,
                payload,
            );
            if (res?.success !== true) {
                this.showErrorMessage(
                    res?.errorMessage ||
                        res?.error?.error ||
                        res?.error?.message ||
                        'Aggiornamento non riuscito.',
                );
                return;
            }
            await this.loadConvocations();
        } finally {
            this.statusUpdatingId = null;
        }
    }
}
